import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";

import styles from "./CategoryPage.module.css";
import Api from "../../utils/Api/Api";
import UTMTracker from "../../utils/UTMTracker";
import { useSeo } from "../../utils/useSeo";
import { buildCategoryPage, buildListingHubs } from "../../seo/categoryPage";
import { TopBar } from "../../components/TopBar/TopBar";
import { isIndia } from "../../utils/market";
import FleekIcon from "../../asset/icons/Brand_logo_Sq.png";

const MAX_PAGES = 10;

// scripts/prerender-meta.js embeds the hub the static page was built from, so
// the first render is that page (same brands, same plans, same prices) and not
// a skeleton replacing it.
//
// A seed belongs to one store: India's hubs (/category/ott) carry India's
// prices, a country's (/us/category/ai-tools, seed.country "US") that store's.
// A page uses only its own store's seed; anything else lists the category from
// the marketplace in the store the visitor is in.
const readSeed = (slug, country = "") => {
  try {
    const el = typeof document !== "undefined" && document.getElementById("category-seed");
    if (!el) return null;
    const seed = JSON.parse(el.textContent || "null");
    if (!seed || seed.slug !== slug || !Array.isArray(seed.hub?.brands) || !seed.hub.brands.length) return null;
    const seedCountry = String(seed.country || "").toUpperCase();
    if (country) return seedCountry === country ? seed : null;
    return !seedCountry && isIndia() ? seed : null;
  } catch {
    return null;
  }
};

/**
 * /category/:slug: every brand in one catalogue category, each with its
 * lowest-priced plan in stock, linking to its brand page. Built by
 * src/seo/categoryPage.js, the same builder the prerender uses. Without a seed
 * (a category added after the build, or the dev server) it lists the category
 * from the marketplace listing, without plans. A slug that matches no hub
 * goes to /marketplace.
 */
export const CategoryPage = ({ country = "" }) => {
  const { slug } = useParams();
  // A country store's own hub (/us/category/ai-tools): its address, links and
  // prices are that store's.
  const prefix = country ? `/${country.toLowerCase()}` : "";
  const home = prefix || "/marketplace";
  const [seed] = useState(() => readSeed(slug, country));
  const [hubs, setHubs] = useState(null);

  useEffect(() => {
    UTMTracker.captureUTMParams();
  }, []);

  useEffect(() => {
    if (readSeed(slug, country)) return undefined;
    let cancelled = false;
    setHubs(null);
    (async () => {
      let all = [];
      try {
        for (let page = 1; page <= MAX_PAGES; page += 1) {
          const { data } = await Api.MarketPlaceData({ page, page_size: 100, digital_only: 1 });
          all = all.concat(data?.results || []);
          if (!data?.next) break;
        }
      } catch (err) {
        console.error("Category listing failed:", err);
      }
      // Outside India each row carries the store's own cheapest plan and price.
      if (!cancelled) setHubs(buildListingHubs(all, { withPrices: !isIndia() }));
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, country]);

  const page = useMemo(() => {
    const seeded = seed && seed.slug === slug ? seed : readSeed(slug, country);
    if (seeded) return buildCategoryPage(seeded.hub, { others: seeded.others, country });
    const abroad = !isIndia();
    if (!hubs) return null;
    const hub = hubs.find((h) => h.slug === slug);
    return hub ? buildCategoryPage(hub, { others: hubs.map((h) => ({ name: h.name, slug: h.slug })), abroad, country }) : null;
  }, [seed, hubs, slug, country]);

  useSeo({
    title: page?.title || "",
    description: page?.metaDescription || "",
    canonical: page?.url || "",
    jsonLd: page?.jsonLd || null,
  });

  const loading = !page && !hubs;
  // Renamed, mistyped or too-thin categories go to the store.
  if (!loading && !page) return <Navigate to={home} replace />;

  return (
    <div className={styles.page}>
      <TopBar title={page?.name || "Category"} backTo={home} />
      <div className={styles.shell}>
        {loading ? (
          <>
            <div className={styles.skelRow} />
            <div className={styles.skelRow} />
            <div className={styles.skelRow} />
          </>
        ) : (
          <>
            <nav className={styles.crumbs} aria-label="Breadcrumb">
              <Link to={home}>Marketplace</Link> › {page.name}
            </nav>
            <h1 className={styles.title}>{page.heading}</h1>
            <p className={styles.intro}>{page.intro}</p>
            <ul className={styles.list}>
              {page.rows.map((r) => (
                <li key={r.slug}>
                  <Link className={styles.card} to={r.href}>
                    <img
                      className={styles.logo}
                      src={r.logo || FleekIcon}
                      alt=""
                      width="44"
                      height="44"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.src = FleekIcon;
                      }}
                    />
                    <span className={styles.main}>
                      <span className={styles.name}>{r.name}</span>
                      {r.available && (
                        <span className={styles.meta}>
                          {r.plan}
                          {r.duration && !r.plan.toLowerCase().includes(r.duration) ? ` · ${r.duration}` : ""}
                        </span>
                      )}
                      {r.known && !r.available && <span className={styles.meta}>Currently unavailable</span>}
                    </span>
                    {r.available && (
                      <span className={styles.prices}>
                        <span className={styles.price}>{r.price}</span>
                        {r.listPrice && <span className={styles.mrp}>{r.listPrice}</span>}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
            {page.others.length > 0 && (
              <section className={styles.others}>
                <h2 className={styles.othersTitle}>Other categories</h2>
                <ul className={styles.chips}>
                  {page.others.map((c) => (
                    <li key={c.slug}>
                      {/* A full load, so each hub opens from its own prerendered page and seed. */}
                      <a className={styles.chip} href={`${prefix}/category/${c.slug}`}>
                        {c.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
